import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Collapse,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Psychology as InsightIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { ReportQuery } from '../../services/reportingService';
import reportingService from '../../services/reportingService';

interface AIInsightsProps {
  query: ReportQuery | null;
  results: unknown[] | null;
  onError: (error: string) => void;
}

interface InsightItem {
  text: string;
  type: 'trend' | 'warning' | 'success' | 'info';
  importance: 'high' | 'medium' | 'low';
}

const AIInsights: React.FC<AIInsightsProps> = ({
  query,
  results,
  onError,
}) => {
  const [insights, setInsights] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [lastAnalyzedQuery, setLastAnalyzedQuery] = useState<string | null>(null);

  const categorizeInsight = (insight: string): InsightItem => {
    const lowerInsight = insight.toLowerCase();
    
    let type: InsightItem['type'] = 'info';
    let importance: InsightItem['importance'] = 'medium';

    // Categorize by keywords
    if (lowerInsight.includes('trend') || lowerInsight.includes('increase') || lowerInsight.includes('growth')) {
      type = 'trend';
    } else if (lowerInsight.includes('risk') || lowerInsight.includes('decline') || lowerInsight.includes('concern')) {
      type = 'warning';
    } else if (lowerInsight.includes('opportunity') || lowerInsight.includes('success') || lowerInsight.includes('improvement')) {
      type = 'success';
    }

    // Determine importance
    if (lowerInsight.includes('significant') || lowerInsight.includes('critical') || lowerInsight.includes('major')) {
      importance = 'high';
    } else if (lowerInsight.includes('minor') || lowerInsight.includes('slight')) {
      importance = 'low';
    }

    return { text: insight, type, importance };
  };

  const getInsightIcon = (type: InsightItem['type']) => {
    switch (type) {
      case 'trend':
        return <TrendingUpIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'success':
        return <SuccessIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getImportanceColor = (importance: InsightItem['importance']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (importance) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const generateInsights = async () => {
    if (!query || !results || results.length === 0) {
      return;
    }

    // Check if we've already analyzed this exact query
    const queryKey = JSON.stringify(query);
    if (queryKey === lastAnalyzedQuery && insights.length > 0) {
      return;
    }

    setLoading(true);
    try {
      const response = await reportingService.generateAIInsights(query, results.slice(0, 100));
      setInsights(response.insights || []);
      setLastAnalyzedQuery(queryKey);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to generate insights');
    } finally {
      setLoading(false);
    }
  };

  const copyInsight = (insight: string) => {
    navigator.clipboard.writeText(insight);
  };

  // Auto-generate insights when query and results change
  useEffect(() => {
    if (query && results && results.length > 0) {
      generateInsights();
    }
  }, [query, results]);

  if (!query || !results) {
    return null;
  }

  const categorizedInsights = insights.map(categorizeInsight);
  const hasHighImportance = categorizedInsights.some(insight => insight.importance === 'high');

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <InsightIcon color="primary" />
            <Typography variant="h6">
              AI Insights
            </Typography>
            {hasHighImportance && (
              <Chip 
                label="High Priority"
                color="error" 
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Refresh insights">
              <IconButton
                onClick={generateInsights}
                disabled={loading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={4}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Analyzing your data for insights...
              </Typography>
            </Box>
          ) : insights.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Based on {results.length} records from {query.entity.replace('_', ' ')}
              </Typography>
              
              <List>
                {categorizedInsights.map((insight, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: insight.importance === 'high' ? 'error.50' : 'background.paper',
                    }}
                    secondaryAction={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={insight.importance}
                          size="small"
                          color={getImportanceColor(insight.importance)}
                          variant="outlined"
                        />
                        <Tooltip title="Copy insight">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => copyInsight(insight.text)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      {getInsightIcon(insight.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={insight.text}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { 
                          fontWeight: insight.importance === 'high' ? 'bold' : 'normal',
                          pr: 2 
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              {insights.length >= 5 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    💡 These insights are AI-generated based on your data patterns. 
                    Consider them as starting points for deeper analysis.
                  </Typography>
                </Alert>
              )}
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <InsightIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No insights available yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Run a query with results to get AI-powered insights
              </Typography>
              {query && results && results.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Your query returned no results. Try adjusting your filters or date range.
                </Alert>
              )}
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AIInsights;